import * as React from "react";
import { Img } from "atoms";
import defaultSRC from "assets/images/default-project-img.svg";
import { Image } from "../types";

// NOTE: Optimization techniques taken from:
// - https://css-tricks.com/a-guide-to-the-responsive-images-syntax-in-html/#using-srcset
// - https://www.11ty.dev/docs/plugins/image/
// - https://jakearchibald.com/2021/serving-sharp-images-to-high-density-screens/

type FeaturedImageProps = Omit<React.ComponentProps<typeof Img>, "src"> & {
  src?: string | Image;
};

const createImgAttributes = (src?: string | Image) => {
  if (!src) {
    return { src: defaultSRC };
  }
  if (typeof src === "string") {
    return { src };
  }
  return {
    src: src.url,
    srcSet: src.url300
      ? `${src.url300} 300w, ${src.url600} 600w, ${src.url1200} 1200w`
      : undefined,
    sizes: src.url300 ? "(min-width: 800px) 50vw, 96vw" : undefined,
  };
};

/**
 * ProjectDetails page featured image component
 */
function FeaturedImage({ src, ...props }: FeaturedImageProps) {
  const [isError, setIsError] = React.useState(false);

  // Build <img> attributes depending on `src` type
  const attrs = createImgAttributes(src);

  // If optimized src doesn't exist for whatever reason, fallback to original src
  const onError: React.ReactEventHandler<HTMLImageElement> = () => {
    // To prevent endless error loop
    if (!isError) setIsError(true);
  };

  return (
    <Img
      {...attrs}
      src={isError ? defaultSRC : attrs.src}
      onError={onError}
      {...props}
    />
  );
}

export { FeaturedImage };
